import { db } from '@/shared/db/database';
import type { ITimelogRepository } from '../../domain/repositories/ITimelogRepository';
import type { Timelog } from '../../domain/entities/Timelog';
import type { TimelogDraft } from '../../domain/entities/TimelogDraft';
import { RemoteTimelogRepository } from './RemoteTimelogRepository';
import { LocalTimelogRepository } from './LocalTimelogRepository';
import { enqueueSyncOperation } from '@/shared/sync/syncQueue';
import { toCreateTimelogRequest } from '../mappers/timelogMapper';
import { classifyApiError } from '@/shared/api/classifyApiError';
import { proveOnline, proveOffline } from '@/shared/onlineState';

/**
 * Thrown by `CompositeTimelogRepository.create` when a timelog cannot be registered.
 * Covers two scenarios:
 *
 * 1. **Online rejection** — the backend actively rejected the request
 *    (400/422/500/duplicate/etc.) while connectivity was still up.
 *    → `'duplicate_timelog'` | `'online_reject'`
 *
 * 2. **Offline consecutive guard** — the local validation detected that the
 *    same person already made a timelog of the SAME accessType within the
 *    last 90 seconds (CONSECUTIVE_MINIMUM_MS). Entrada→salida is always OK,
 *    but entrada→entrada or salida→salida within 1.5 min is blocked.
 *    → `'consecutive_offline'` with `elapsedSeconds`
 *
 * In both cases the timelog MUST NOT be persisted (no phantom rows).
 */
export type OnlineTimelogErrorCode = 'duplicate_timelog' | 'online_reject' | 'consecutive_offline';

export class OnlineTimelogError extends Error {
  readonly code: OnlineTimelogErrorCode;
  /** Seconds elapsed since the last timelog (only for consecutive_offline). */
  readonly elapsedSeconds: number | undefined;
  /** Minimum required seconds (only for consecutive_offline). */
  readonly minSeconds: number | undefined;
  constructor(code: OnlineTimelogErrorCode, message: string, elapsedSeconds?: number, minSeconds?: number) {
    super(message);
    this.name = 'OnlineTimelogError';
    this.code = code;
    this.elapsedSeconds = elapsedSeconds;
    this.minSeconds = minSeconds;
  }
}

export class CompositeTimelogRepository implements ITimelogRepository {
  private remote = new RemoteTimelogRepository();
  private local  = new LocalTimelogRepository();

  async create(
    draft: TimelogDraft,
    consecutiveMinSeconds?: number,
  ): Promise<Timelog | { virtualSuccess: true; localId: string }> {
    // Offline consecutive guard: same accessType within 1.5 min → reject
    // Only applies to offline drafts — online calls have backend validation.
    if (draft.isOffline) {
      await this.assertNotConsecutive(draft, consecutiveMinSeconds);
    }

    // Always persist locally first (offline-first)
    await this.local.save(draft);

    if (draft.isOffline) {
      // Idempotent enqueue: use draft.localId as the syncQueue PK so that
      // calling create() twice with the same draft never produces duplicate rows.
      await enqueueSyncOperation({
        id:        draft.localId,
        module:    'timelog',
        operation: 'create',
        endpoint:  `/v1/clock/app/entity/${draft.idEntity}/timelogs`,
        payload:   toCreateTimelogRequest(draft),
      });
      return { virtualSuccess: true, localId: draft.localId };
    }

    try {
      const result = await this.remote.create(draft);
      if ('idTimelog' in result) {
        await this.local.markSynced(draft.localId, result.idTimelog);
      }
      // Backend request succeeded → mark as online for future requests
      proveOnline();
      return result;
    } catch (err) {
      // Two very different failures can land here. The connectivity check
      // right after the failure is what tells them apart:
      //
      //  1) Connectivity dropped mid-POST (network cable pulled, wifi lost,
      //     timeout after request was already flying). `navigator.onLine`
      //     will now be false. → Degrade to the offline path: markFailed
      //     + enqueue + virtualSuccess so the sync worker can push it
      //     when the network comes back. The user sees success. This is
      //     the offline-first contract of the whole app.
      //
      //  2) Connectivity is still up. The backend actually rejected the
      //     request (400 duplicate, 422 validation, 500 server error, …).
      //     Retrying with the same payload will always fail. → Delete the
      //     local draft (no phantom row in listings) and throw so the UI
      //     can show a real error page. `code` distinguishes duplicate
      //     rejections from generic online rejections in case pages want
      //     specific copy.
      
      const apiError = classifyApiError(err);

      // Network/timeout error → backend unreachable, mark offline
      if (apiError.kind === 'network' || apiError.kind === 'timeout' || !navigator.onLine) {
        proveOffline();
        const msg = err instanceof Error ? err.message : String(err);
        await this.local.markFailed(draft.localId, msg);
        await enqueueSyncOperation({
          id:        draft.localId,
          module:    'timelog',
          operation: 'create',
          endpoint:  `/v1/clock/app/entity/${draft.idEntity}/timelogs`,
          payload:   toCreateTimelogRequest(draft),
        });
        return { virtualSuccess: true, localId: draft.localId };
      }

      // Real business rejection from backend (duplicate, validation, 500)
      const code: OnlineTimelogErrorCode =
        apiError.kind === 'business' && apiError.code === 'duplicate_timelog'
          ? 'duplicate_timelog'
          : 'online_reject';

      await this.local.delete(draft.localId);
      throw new OnlineTimelogError(code, apiError.message);
    }
  }

  /**
   * Offline consecutive-timelog guard.
    *
    * Finds the LAST timelog for this person (ANY accessType). If that last
    * timelog was made within the configured minimum seconds (90-300s, default 90s),
    * throws `OnlineTimelogError('consecutive_offline')` with the elapsed seconds
    * so the error page can show a precise message.
    *
    * The rule is: ANY marcaje blocks ALL marcajes for X seconds.
    *
    * Example (90s threshold):
    *   T=0:   entrada ✅
    *   T=30s: salida ❌ (blocked, only 30s since last marcaje)
    *   T=100s: salida ✅ (>90s since T=0)
    *   T=120s: entrada ❌ (blocked, only 20s since T=100)
    *   T=200s: entrada ✅ (>90s since T=100)
    *
    * Called BEFORE `local.save()` so no phantom draft is created when the
    * guard fires.
    */
  private async assertNotConsecutive(draft: TimelogDraft, consecutiveMinSeconds?: number): Promise<void> {
    // Resolve idPerson from idEntity (same lookup as LocalTimelogRepository.save)
    let idPerson = 0;
    try {
      const allEmployees = await db.employees.toArray();
      const employee = allEmployees.find((e) => {
        const dto = e.raw as { id_entity: number };
        return dto.id_entity === draft.idEntity;
      });
      if (employee) idPerson = employee.idPerson;
    } catch {
      // If employee lookup fails, skip the guard rather than blocking the timelog
      return;
    }

    if (idPerson <= 0) return;

    // Find the LAST timelog regardless of accessType
    const lastLog = await this.local.getLastByPerson(idPerson);
    if (!lastLog) return;

    const elapsed = Date.now() - new Date(lastLog.datetime).getTime();
    const minMs = (consecutiveMinSeconds ?? 90) * 1000;
    const minSeconds = consecutiveMinSeconds ?? 90;
    
    // Guard: if elapsed is negative (lastLog in the future), skip validation
    // This happens when device clock is behind or server timestamp is ahead
    if (elapsed < 0) return;
    
    if (elapsed < minMs) {
      const seconds = Math.floor(elapsed / 1000);
      throw new OnlineTimelogError(
        'consecutive_offline',
        `Deben transcurrir al menos ${minSeconds} segundos entre marcajes. Solo pasaron ${seconds}s.`,
        seconds,
        minSeconds,
      );
    }
  }

  async listByAccessRole(accessRoleId: number, from: string, to: string): Promise<Timelog[]> {
    return this.remote.listByAccessRole(accessRoleId, from, to);
  }

  /**
   * Two-flow timelog query with universal merge.
   *
   * Flow 1 (online): Calls the remote API, merges the result into the local Dexie
   * `remoteTimelogs` table via `bulkUpsert`, THEN merges pending/failed drafts
   * from `db.timelogs` (Fix 3). Returns combined remote + drafts, sorted descending.
   *
   * Flow 2 (offline/fallback): On any network error, reads directly from the local
   * `remoteTimelogs` table AND `db.timelogs` (pending/failed drafts), merges them,
   * and returns the combined result without throwing.
   *
   * Both flows now return consistent data: remote + pending drafts.
   *
   * @param filters.accessRoleId - The access role whose timelogs to fetch.
   * @param filters.startDate    - ISO date string (inclusive lower bound).
   * @param filters.endDate      - ISO date string (inclusive upper bound).
   * @returns Timelogs from remote + pending drafts (online), or cached + pending (offline).
   */
  async fetchAllWithFallback(filters: {
    accessRoleId: number;
    startDate: string;
    endDate: string;
  }): Promise<Timelog[]> {
    try {
      const remote = await this.remote.listByAccessRole(
        filters.accessRoleId,
        filters.startDate,
        filters.endDate
      );

      // Merge to local cache
      await this.local.bulkUpsert(
        remote,
        filters.accessRoleId,
        filters.startDate,
        filters.endDate
      );

      // Fix 3: Merge pending/failed drafts from db.timelogs
      const drafts = await this.local.getDraftsByDateRange(
        filters.accessRoleId,
        filters.startDate,
        filters.endDate
      );
      
      // Combine remote + drafts, sort descending by datetime
      const combined = [...remote, ...drafts];
      combined.sort((a, b) => {
        const aTime = a.datetime || '';
        const bTime = b.datetime || '';
        return bTime.localeCompare(aTime);
      });
      
      return combined;
    } catch (error) {
      const local = await this.local.fetchByDateRange(
        filters.accessRoleId,
        filters.startDate,
        filters.endDate
      );
      return local;
    }
  }

  /**
   * Incremental fetch used by the background refresh scheduler.
   *
   * Fetches all timelogs from `since` up to `now` for the given access role,
   * then merges them into the local cache via `bulkUpsert`. The scheduler calls
   * this every 5 minutes, using `cachedRanges.lastFetchedAt` as the `since`
   * value to avoid re-fetching data that was already downloaded.
   *
   * Unlike `fetchAllWithFallback`, this method does NOT fall back to local cache —
   * it is only called when an active network connection is expected.
   *
   * @param accessRoleId - The access role to refresh.
   * @param since        - ISO date string; only timelogs at or after this time are fetched.
   * @returns Newly fetched timelogs (may be empty if no events since `since`).
   */
  async fetchIncremental(accessRoleId: number, since: string): Promise<Timelog[]> {
    const now = new Date().toISOString();
    const remote = await this.remote.listByAccessRole(accessRoleId, since, now);

    // Merge to local cache
    await this.local.bulkUpsert(remote, accessRoleId, since, now);

    return remote;
  }

  async getLastByPerson(idPerson: number): Promise<Timelog | null> {
    return this.local.getLastByPerson(idPerson);
  }

  async getLastByPersonAndAccessType(
    idPerson: number,
    accessType: 1 | 2,
  ): Promise<Timelog | null> {
    return this.local.getLastByPersonAndAccessType(idPerson, accessType);
  }
}
